import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../utils/i18n';
import { Plus, Search, User, Phone, Mail, MapPin, ArrowLeft } from 'lucide-react';
import './CustomerListPage.css';

const CustomerListPage = () => {
  const navigate = useNavigate();
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await customerAPI.getAll();
      // Handle different response structures
      const customersData = response.data?.customers || response.customers || response.data || [];
      setCustomers(Array.isArray(customersData) ? customersData : []);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      // Mock data for development
      setCustomers([
        {
          id: 1,
          name: 'Nguyễn Văn A',
          idNumber: '001234567890',
          phone: '+84912345678',
          email: 'nguyenvana@email.com',
          address: '123 Đường ABC, Quận 1, TPHCM',
          country: 'Vietnam',
          createdAt: '2024-11-01T10:00:00Z',
        },
        {
          id: 2,
          name: 'Trần Thị B',
          idNumber: '009876543210',
          phone: '+84987654321',
          email: 'tranthib@email.com',
          address: '456 Đường XYZ, Quận 2, TPHCM',
          country: 'Vietnam',
          createdAt: '2024-11-05T14:30:00Z',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredCustomers = Array.isArray(customers)
    ? customers.filter((customer) =>
        customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone?.includes(searchTerm) ||
        customer.idNumber?.includes(searchTerm)
      )
    : [];

  const handleCustomerClick = (id) => {
    navigate(`/loan/customers/edit/${id}`);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="customer-list-page">
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/work')}>
          <ArrowLeft size={20} />
        </button>
        <h1 className="page-title">{t('customers')}</h1>
      </div>

      {/* Floating Action Button */}
      <button
        className="fab"
        onClick={() => navigate('/loan/customers/new')}
        aria-label={t('add')}
      >
        <Plus size={24} />
      </button>

      <div className="page-content">
        {/* Search */}
        <div className="search-box">
          <Search className="search-icon" size={20} />
          <input
            type="text"
            placeholder={t('searchCustomer')}
            value={searchTerm}
            onChange={handleSearch}
            className="search-input"
          />
        </div>

        {/* Customer List */}
        {filteredCustomers.length === 0 ? (
          <div className="empty-state">
            <User size={48} />
            <p>
              {searchTerm
                ? t('noCustomersFound')
                : t('noCustomersYet')}
            </p>
            {!searchTerm && (
              <button
                className="btn btn-primary"
                onClick={() => navigate('/loan/customers/new')}
              >
                <Plus size={20} />
                {t('addCustomer')}
              </button>
            )}
          </div>
        ) : (
          <div className="customer-list">
            {filteredCustomers.map((customer) => (
              <div
                key={customer.id}
                className="customer-card"
                onClick={() => handleCustomerClick(customer.id)}
              >
                <div className="customer-avatar">
                  <User size={24} />
                </div>

                <div className="customer-info">
                  <h3 className="customer-name">{customer.name}</h3>

                  <div className="customer-detail">
                    <Phone size={14} />
                    <span>{customer.phone}</span>
                  </div>

                  {customer.email && (
                    <div className="customer-detail">
                      <Mail size={14} />
                      <span>{customer.email}</span>
                    </div>
                  )}

                  <div className="customer-detail">
                    <MapPin size={14} />
                    <span className="customer-address">{customer.address}</span>
                  </div>

                  <div className="customer-meta">
                    <span className="customer-id">{t('idNumber')}: {customer.idNumber}</span>
                    <span className="customer-date">
                      {new Date(customer.createdAt).toLocaleDateString(language === 'vi' ? 'vi-VN' : language === 'ko' ? 'ko-KR' : 'en-US')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerListPage;
